import { vec3 } from 'gl-matrix';

import macro from 'vtk.js/Sources/macros';
import vtkImageData from 'vtk.js/Sources/Common/DataModel/ImageData';
import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
import {debugCallback} from "./Common";

const { vtkErrorMacro } = macro;

const isDebugging = false

function debug(callback) {
    debugCallback(isDebugging, callback);
}

//Corresponds to the SegmentationPaintingMode DU in F#.
export const SegmentationPaintingMode = Object.freeze({
    PAINT: "Paint",
    BUMP: "Bump",
    ERASE: "Erase",
});

// ----------------------------------------------------------------------------
// vtkPaintFilter methods
// ----------------------------------------------------------------------------

function vtkPaintFilter(publicAPI, model) {
    // Set our className
    model.classHierarchy.push('vtkPaintFilter');

    const history = {};
    let strokeIndices = [];
    let strokeValues = [];

    // --------------------------------------------------------------------------

    function resetHistory() {
        history.index = -1;
        history.snapshots = [];
        history.labels = [];
    }

    function pushToHistory(snapshot, label) {
        // Clear any "redo" info
        const spliceIndex = history.index + 1;
        const spliceLength = history.snapshots.length - history.index;
        history.snapshots.splice(spliceIndex, spliceLength);
        history.labels.splice(spliceIndex, spliceLength);

        // Push new snapshot
        history.snapshots.push(snapshot);
        history.labels.push(label);
        history.index++;
    }

    publicAPI.setPaintingMode = (paintingMode) => {
        model.paintingMode = paintingMode;
    };

    publicAPI.setLabelForClickedPoint = (point, controlKey) => {
        if (!model.labelMap || !model.maskWorldToIndex) {
            return;
        }

        if (model.paintingMode === SegmentationPaintingMode.PAINT) {
            model.label = controlKey ? 0 : model.paintLabel;
            return;
        } else if (model.paintingMode === SegmentationPaintingMode.ERASE) {
            model.label = 0;
            return;
        }
        // Check if the label map for the current slice contains both background (0) and mask
        // values. If only one type exists, toggle. Treat values 1 and 2 both as "mask on"
        // (value 2 is the edit label, rendered yellow). When painting in BUMP mode, always
        // use the edit label (2) so bumped strokes are visually distinct.

        // Convert world point to index coordinates to determine the slice.
        const indexPt = [0, 0, 0];
        vec3.transformMat4(indexPt, point, model.maskWorldToIndex);
        indexPt[0] = Math.round(indexPt[0]);
        indexPt[1] = Math.round(indexPt[1]);
        indexPt[2] = Math.round(indexPt[2]);

        // Get dimensions and calculate slice bounds
        const dimensions = model.labelMap.getDimensions();
        const [nx, ny, nz] = dimensions;
        if (nx === 0 || ny === 0 || nz === 0) {
            return;
        }

        const clampedIndexPt = [
            Math.min(Math.max(indexPt[0], 0), nx - 1),
            Math.min(Math.max(indexPt[1], 0), ny - 1),
            Math.min(Math.max(indexPt[2], 0), nz - 1),
        ];
        const sliceIndex = clampedIndexPt[model.slicingMode];

        // Get the full data array
        const data = model.labelMap.getPointData().getScalars().getData();
        const pickedLabel = data[clampedIndexPt[0] + clampedIndexPt[1] * nx + clampedIndexPt[2] * nx * ny];

        // Calculate slice parameters based on slicing mode.
        let sliceSize, offset;
        if (model.slicingMode === 2) {
            // Z-axis (axial) - slice through XY plane.
            sliceSize = nx * ny;
            offset = sliceIndex * sliceSize;
        } else if (model.slicingMode === 1) {
            // Y-axis (coronal) - slice through XZ plane.
            sliceSize = nx * nz;
            offset = 0;
            // For Y-axis, data is not contiguous in memory, need different approach.
        } else if (model.slicingMode === 0) {
            // X-axis (sagittal) - slice through YZ plane.
            sliceSize = ny * nz;
            offset = 0;
            // For X-axis, data is not contiguous in memory, need different approach.
        }

        let hasZero = false;
        let hasMask = false; 

        // Check only the current slice. As soon as both background (0) and mask values
        // are seen, the slice is mixed — set the label and return immediately.
        if (model.slicingMode === 2) {
            // Z-axis: data is contiguous.
            const sliceData = data.subarray(offset, offset + sliceSize);
            for (let i = 0; i < sliceData.length; i++) {
                if (sliceData[i] === 0) {
                    hasZero = true;
                } else if (sliceData[i] !== 0) {
                    hasMask = true;
                }

                if (hasZero && hasMask) {
                    model.label = (pickedLabel === 0) ? 0 : model.paintLabel;
                    return;
                }
            }
        } else if (model.slicingMode === 1) {
            // Y-axis: iterate through non-contiguous data.
            for (let z = 0; z < nz; z++) {
                for (let x = 0; x < nx; x++) {
                    const idx = x + sliceIndex * nx + z * nx * ny;
                    if (data[idx] === 0) {
                        hasZero = true;
                    } else if (data[idx] !== 0) {
                        hasMask = true;
                    }

                    if (hasZero && hasMask) {
                        model.label = (pickedLabel === 0) ? 0 : model.paintLabel;
                        return;
                    }
                }
            }
        } else if (model.slicingMode === 0) {
            // X-axis: iterate through non-contiguous data.
            for (let z = 0; z < nz; z++) {
                for (let y = 0; y < ny; y++) {
                    const idx = sliceIndex + y * nx + z * nx * ny;
                    if (data[idx] === 0) {
                        hasZero = true;
                    } else if (data[idx] !== 0) {
                        hasMask = true;
                    }

                    if (hasZero && hasMask) {
                        model.label = (pickedLabel === 0) ? 0 : model.paintLabel;
                        return;
                    }
                }
            }
        }

        // Slice is uniform — only background, or only mask.
        model.label = hasMask ? 0 : model.paintLabel;
    };

    // --------------------------------------------------------------------------

    // Reads the background (HU) value at a world point. Returns null if the volume or
    // transform isn't ready. Reuses the world -> index -> round -> clamp pattern from
    // setLabelForClickedPoint. (Gate 0: used for the HU readout; later gates seed the grow.)
    publicAPI.getHuAtWorldPoint = (worldPoint) => {
        if (!model.backgroundImage || !model.maskWorldToIndex) {
            return null;
        }

        const [nx, ny, nz] = model.backgroundImage.getDimensions();
        if (nx === 0 || ny === 0 || nz === 0) {
            return null;
        }

        const indexPt = [0, 0, 0];
        vec3.transformMat4(indexPt, worldPoint, model.maskWorldToIndex);
        const x = Math.min(Math.max(Math.round(indexPt[0]), 0), nx - 1);
        const y = Math.min(Math.max(Math.round(indexPt[1]), 0), ny - 1);
        const z = Math.min(Math.max(Math.round(indexPt[2]), 0), nz - 1);

        const bg = model.backgroundImage.getPointData().getScalars().getData();
        return { x, y, z, hu: bg[x + y * nx + z * nx * ny] };
    };

    // --------------------------------------------------------------------------

    // HU SELECT — 2D flood fill on the clicked axial slice only (Gate 1).
    // From a seed world point, fills all 4-connected voxels on that slice whose
    // background HU is within ±tolerance of the seed's HU, writing the edit label into the
    // labelMap. Defaults to `model.paintLabel` — the SAME value the brush stamps (2 on axial),
    // which the render opacity makes visible everywhere — so the fill ADDS a visible selection
    // anywhere on the slice (writing the original mask's label instead would overwrite/erase
    // existing mask voxels and not render outside it). Records changed voxels into the stroke
    // history (undo/redo). Returns { count, sliceIdx }; count 0 / sliceIdx -1 means nothing filled.
    publicAPI.growSlice2D = (worldPoint, tolerance, labelOverride) => {
        const label = labelOverride ?? model.paintLabel;
        if (!model.labelMap || !model.maskWorldToIndex || !model.backgroundImage) {
            return { count: 0, sliceIdx: -1 };
        }

        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        const bg = model.backgroundImage.getPointData().getScalars().getData();
        const [nx, ny, nz] = model.labelMap.getDimensions();
        if (nx === 0 || ny === 0 || nz === 0) {
            return { count: 0, sliceIdx: -1 };
        }

        // Seed voxel (world -> index -> round -> clamp), same as getHuAtWorldPoint.
        const seedIndexPt = [0, 0, 0];
        vec3.transformMat4(seedIndexPt, worldPoint, model.maskWorldToIndex);
        const sx = Math.min(Math.max(Math.round(seedIndexPt[0]), 0), nx - 1);
        const sy = Math.min(Math.max(Math.round(seedIndexPt[1]), 0), ny - 1);
        const sz = Math.min(Math.max(Math.round(seedIndexPt[2]), 0), nz - 1);

        const seedHU = bg[sx + sy * nx + sz * nx * ny];
        const lo = seedHU - tolerance;
        const hi = seedHU + tolerance;

        // Set model.label so the stroke history records the right value for redo.
        model.label = label;

        // Fresh stroke so undo reverts this fill in one step.
        publicAPI.startStroke();

        // BFS confined to slice z = sz; index within the slice = x + y*nx (+ sliceOffset).
        const sliceOffset = sz * nx * ny;
        const visited = new Uint8Array(nx * ny);
        const stack = [sx + sy * nx];
        visited[sx + sy * nx] = 1;
        let count = 0;

        while (stack.length > 0) {
            const p = stack.pop(); // p = x + y*nx within the slice
            const idx = sliceOffset + p;

            const hu = bg[idx];
            if (hu < lo || hu > hi) continue;

            if (data[idx] !== label) {
                strokeIndices.push(idx);
                strokeValues.push(data[idx]);
                data[idx] = label;
            }
            count++;

            const x = p % nx;
            const y = (p / nx) | 0;
            if (x > 0 && !visited[p - 1]) { visited[p - 1] = 1; stack.push(p - 1); }
            if (x < nx - 1 && !visited[p + 1]) { visited[p + 1] = 1; stack.push(p + 1); }
            if (y > 0 && !visited[p - nx]) { visited[p - nx] = 1; stack.push(p - nx); }
            if (y < ny - 1 && !visited[p + nx]) { visited[p + nx] = 1; stack.push(p + nx); }
        }

        // Commit the stroke into history, then trigger a re-render.
        publicAPI.endStroke();
        scalars.modified();
        model.labelMap.modified();
        publicAPI.modified();

        return { count, sliceIdx: count > 0 ? sz : -1 };
    };

    // --------------------------------------------------------------------------

    // HU SELECT — 3D flood fill (Gate 3). Same as growSlice2D but propagates through the
    // WHOLE volume with 6-connectivity (±x, ±y, ±z), so one click selects the connected
    // similar-HU structure across slices. Writes model.paintLabel (always-visible edit
    // label), records stroke history for one-step undo. A voxel cap bounds worst-case cost
    // so a wide-tolerance click on soft tissue can't hang the UI thread.
    // Returns { count, zMin, zMax }; count 0 / zMax -1 means nothing filled.
    publicAPI.growVolume3D = (worldPoint, tolerance, options = {}) => {
        const label = options.label ?? model.paintLabel;
        const maxVoxels = options.maxVoxels ?? 2_000_000;
        // onlyReplace: if set, only overwrite voxels whose CURRENT label equals this value.
        // Used by the eraser to clear only HU-select additions (edit label) while leaving
        // the original mask intact. null = overwrite anything (normal select). The BFS still
        // traverses the whole in-band region — this gates only the write, not the traversal.
        const onlyReplace = options.onlyReplace ?? null;
        // Spatial bound: a LOOSE safety net so a runaway grow can't sprawl across the body.
        // The edge-stop below is the primary shaper, so the box should rarely show.
        const maxRadius = options.maxRadiusVoxels ?? 60;
        // Edge-stop: don't propagate across a strong HU edge. Following the actual
        // intensity boundary (instead of a geometric box) avoids the square-edge artifacts
        // and hugs the real structure. 0/false disables it.
        const edgeThreshold = options.edgeThreshold ?? 150;
        // keepStroke: when true, do NOT open/close a stroke here — the caller manages one
        // long stroke across many calls (hold-to-grow ticks) so a single undo reverts it all.
        const keepStroke = options.keepStroke ?? false;
        if (!model.labelMap || !model.maskWorldToIndex || !model.backgroundImage) {
            return { count: 0, zMin: -1, zMax: -1 };
        }

        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        const bg = model.backgroundImage.getPointData().getScalars().getData();
        const [nx, ny, nz] = model.labelMap.getDimensions();
        if (nx === 0 || ny === 0 || nz === 0) {
            return { count: 0, zMin: -1, zMax: -1 };
        }
        const nxny = nx * ny;

        // Seed voxel (world -> index -> round -> clamp).
        const seedIndexPt = [0, 0, 0];
        vec3.transformMat4(seedIndexPt, worldPoint, model.maskWorldToIndex);
        const sx = Math.min(Math.max(Math.round(seedIndexPt[0]), 0), nx - 1);
        const sy = Math.min(Math.max(Math.round(seedIndexPt[1]), 0), ny - 1);
        const sz = Math.min(Math.max(Math.round(seedIndexPt[2]), 0), nz - 1);

        const seedIdx = sx + sy * nx + sz * nxny;
        const seedHU = bg[seedIdx];
        const lo = seedHU - tolerance;
        const hi = seedHU + tolerance;

        // Spherical bound: confine the grow to within maxRadius (Euclidean) of the seed.
        // A sphere instead of a box avoids the square-edge artifact where the cutoff shows.
        const maxRadiusSq = maxRadius * maxRadius;

        model.label = label;
        if (!keepStroke) publicAPI.startStroke();

        const visited = new Uint8Array(nx * ny * nz);
        const stack = [seedIdx];
        visited[seedIdx] = 1;

        let count = 0; // voxels actually written (added/erased)
        let visitedCount = 0; // voxels traversed (for the safety cap)
        let zMin = nz;
        let zMax = -1;
        let capped = false;

        while (stack.length > 0) {
            const idx = stack.pop();

            const hu = bg[idx];
            if (hu < lo || hu > hi) continue;

            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;

            // Write only where allowed (onlyReplace gates the write, not the traversal).
            // count / zMin / zMax track WRITTEN voxels so the commit slice-range is correct.
            if (
                (onlyReplace === null || data[idx] === onlyReplace) &&
                data[idx] !== label
            ) {
                strokeIndices.push(idx);
                strokeValues.push(data[idx]);
                data[idx] = label;
                count++;
                if (z < zMin) zMin = z;
                if (z > zMax) zMax = z;
            }

            // Cap on traversal (not writes) so cost is bounded even when few voxels match.
            visitedCount++;
            if (visitedCount >= maxVoxels) {
                capped = true;
                break;
            }

            // Push a neighbor only if: unvisited, within the spherical radius of the seed,
            // and NOT across a strong HU edge from this voxel (the edge-stop that shapes the
            // selection to the structure boundary).
            const tryPush = (nIdx, nxc, nyc, nzc) => {
                if (visited[nIdx]) return;
                visited[nIdx] = 1;
                const dx = nxc - sx;
                const dy = nyc - sy;
                const dz = nzc - sz;
                if (dx * dx + dy * dy + dz * dz > maxRadiusSq) return;
                if (edgeThreshold > 0 && Math.abs(bg[nIdx] - hu) > edgeThreshold) return;
                stack.push(nIdx);
            };
            if (x > 0) tryPush(idx - 1, x - 1, y, z);
            if (x < nx - 1) tryPush(idx + 1, x + 1, y, z);
            if (y > 0) tryPush(idx - nx, x, y - 1, z);
            if (y < ny - 1) tryPush(idx + nx, x, y + 1, z);
            if (z > 0) tryPush(idx - nxny, x, y, z - 1);
            if (z < nz - 1) tryPush(idx + nxny, x, y, z + 1);
        }

        if (capped) {
            debug(() =>
                vtkErrorMacro(
                    `growVolume3D: hit voxel cap (${maxVoxels}); selection stopped early`,
                ),
            );
        }

        if (!keepStroke) publicAPI.endStroke();
        scalars.modified();
        model.labelMap.modified();
        publicAPI.modified();

        return { count, zMin: zMax < 0 ? -1 : zMin, zMax };
    };

    // --------------------------------------------------------------------------

    // EDGE-SEEDED DILATION (hold-to-grow Step 2). beginDilate finds the connected
    // edit-label blob under the click and seeds a frontier from its BOUNDARY voxels;
    // each dilateStep() advances that frontier one layer outward (uniform from every
    // edge), gated by the ±tolerance HU band + strong-edge stop. State persists on
    // model._dilateState across ticks so the caller (a hold timer) inflates the mask
    // evenly. The caller owns the stroke (startStroke before, endStroke after) so the
    // whole hold is one undo. Returns false if the click isn't on an edit-label voxel
    // (caller should fall back to the point-seeded growVolume3D).
    publicAPI.beginDilate = (worldPoint, tolerance, options = {}) => {
        const editLabel = options.editLabel ?? model.paintLabel;
        // Re-anchoring band: the grow follows the ADVANCING edge, not the fixed seed. Two
        // HU tests replace the old seed-anchored band + edgeThreshold (see dilateStep):
        //  - local step: a candidate must be within `tolerance` of the frontier voxel it
        //    grows from (|nHU - curHU| ≤ tolerance) → follows tissue whose density drifts.
        //  - outer clamp: a candidate must still be within `tolerance × driftMult` of the
        //    ORIGINAL seed HU → a slow walk can follow a gradient, but a leap into far-off
        //    density is blocked.
        const driftMult = options.driftMult ?? 4;
        // Round the dilation: cap each shell by Euclidean distance from the ORIGINAL mask
        // edge (uniform from every edge). Without this the layered 6-connected frontier
        // grows as an L1 ball = a diamond. maxRadiusVoxels bounds how far past the edge.
        const maxRadius = options.maxRadiusVoxels ?? 60;
        const maxRadiusSq = maxRadius * maxRadius;
        if (!model.labelMap || !model.maskWorldToIndex || !model.backgroundImage) {
            return false;
        }
        const data = model.labelMap.getPointData().getScalars().getData();
        const bg = model.backgroundImage.getPointData().getScalars().getData();
        const [nx, ny, nz] = model.labelMap.getDimensions();
        if (nx === 0 || ny === 0 || nz === 0) return false;
        const nxny = nx * ny;

        const seedIndexPt = [0, 0, 0];
        vec3.transformMat4(seedIndexPt, worldPoint, model.maskWorldToIndex);
        const sx = Math.min(Math.max(Math.round(seedIndexPt[0]), 0), nx - 1);
        const sy = Math.min(Math.max(Math.round(seedIndexPt[1]), 0), ny - 1);
        const sz = Math.min(Math.max(Math.round(seedIndexPt[2]), 0), nz - 1);
        const seedIdx = sx + sy * nx + sz * nxny;

        // Edge-seeding only applies when clicking ON an existing selection.
        if (data[seedIdx] !== editLabel) return false;

        // seedHU anchors the WIDE outer clamp; stepTolerance is the local step threshold.
        const seedHU = bg[seedIdx];
        const stepTolerance = tolerance;
        const seedLo = seedHU - tolerance * driftMult;
        const seedHi = seedHU + tolerance * driftMult;

        // BFS the connected edit-label blob; mark visited (already selected — don't rewrite).
        const visited = new Uint8Array(nx * ny * nz);
        const blob = [seedIdx];
        visited[seedIdx] = 1;
        for (let i = 0; i < blob.length; i++) {
            const idx = blob[i];
            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            const pushIfBlob = (nIdx) => {
                if (!visited[nIdx] && data[nIdx] === editLabel) {
                    visited[nIdx] = 1;
                    blob.push(nIdx);
                }
            };
            if (x > 0) pushIfBlob(idx - 1);
            if (x < nx - 1) pushIfBlob(idx + 1);
            if (y > 0) pushIfBlob(idx - nx);
            if (y < ny - 1) pushIfBlob(idx + nx);
            if (z > 0) pushIfBlob(idx - nxny);
            if (z < nz - 1) pushIfBlob(idx + nxny);
        }

        // Frontier = blob voxels that have at least one non-edit neighbor (the boundary).
        // Parallel arrays: frontier[] = voxel idx, origin{X,Y,Z}[] = the boundary voxel this
        // branch grew from (each boundary voxel is its own origin, distance 0). The origin is
        // carried forward each layer so a shell's thickness is measured from the fixed edge.
        const frontier = [];
        const originX = [];
        const originY = [];
        const originZ = [];
        for (let i = 0; i < blob.length; i++) {
            const idx = blob[i];
            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            const onBoundary =
                (x > 0 && data[idx - 1] !== editLabel) ||
                (x < nx - 1 && data[idx + 1] !== editLabel) ||
                (y > 0 && data[idx - nx] !== editLabel) ||
                (y < ny - 1 && data[idx + nx] !== editLabel) ||
                (z > 0 && data[idx - nxny] !== editLabel) ||
                (z < nz - 1 && data[idx + nxny] !== editLabel);
            if (onBoundary) {
                frontier.push(idx);
                originX.push(x);
                originY.push(y);
                originZ.push(z);
            }
        }

        model._dilateState = {
            visited,
            frontier,
            originX,
            originY,
            originZ,
            maxRadiusSq,
            seedHU,
            stepTolerance,
            driftMult,
            seedLo,
            seedHi,
            editLabel,
            nx,
            ny,
            nz,
            nxny,
        };
        return true;
    };

    // Advance the dilation frontier by ONE layer. Returns { count, zMin, zMax } for the
    // voxels written this step (count 0 / zMax -1 means the dilation can't grow further).
    // Pass a tolerance to re-derive the HU band for this step (adaptive-tolerance control):
    // it becomes the local step threshold AND rescales the wide seed clamp (× driftMult).
    // Pass maxRadiusVoxels to widen the round distance cap for this step (grow-while-held):
    // the Euclidean cap keeps each shell round; growing it across ticks extends the reach.
    publicAPI.dilateStep = (tolerance, maxRadiusVoxels) => {
        const st = model._dilateState;
        if (!st) return { count: 0, zMin: -1, zMax: -1 };
        if (typeof tolerance === "number") {
            st.stepTolerance = tolerance;
            st.seedLo = st.seedHU - tolerance * st.driftMult;
            st.seedHi = st.seedHU + tolerance * st.driftMult;
        }
        if (typeof maxRadiusVoxels === "number") {
            st.maxRadiusSq = maxRadiusVoxels * maxRadiusVoxels;
        }
        const {
            visited,
            editLabel,
            stepTolerance,
            seedLo,
            seedHi,
            maxRadiusSq,
            nx,
            ny,
            nz,
            nxny,
        } = st;
        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        const bg = model.backgroundImage.getPointData().getScalars().getData();

        model.label = editLabel;

        // Next layer's frontier, carrying each accepted voxel's origin (the fixed boundary
        // voxel its branch grew from) so distance is always measured from the original edge.
        const nextFrontier = [];
        const nextOriginX = [];
        const nextOriginY = [];
        const nextOriginZ = [];
        let count = 0;
        let zMin = nz;
        let zMax = -1;

        const tryAdvance = (nIdx, curHU, ox, oy, oz) => {
            if (visited[nIdx]) return;
            visited[nIdx] = 1;
            const nHU = bg[nIdx];
            // Local step: the candidate must be within stepTolerance of the frontier voxel
            // it grows from (re-anchored band → follows drifting density).
            if (Math.abs(nHU - curHU) > stepTolerance) return;
            // Outer clamp: still within the WIDE band around the original seed (blocks a
            // slow walk from leaping into far-off density).
            if (nHU < seedLo || nHU > seedHi) return;
            // Round cap: reject beyond maxRadius (Euclidean) from this branch's origin edge.
            const z = (nIdx / nxny) | 0;
            const rem = nIdx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            const dx = x - ox;
            const dy = y - oy;
            const dz = z - oz;
            if (dx * dx + dy * dy + dz * dz > maxRadiusSq) return;
            // Accept: write the edit label, record undo, carry into next layer (same origin).
            strokeIndices.push(nIdx);
            strokeValues.push(data[nIdx]);
            data[nIdx] = editLabel;
            nextFrontier.push(nIdx);
            nextOriginX.push(ox);
            nextOriginY.push(oy);
            nextOriginZ.push(oz);
            count++;
            if (z < zMin) zMin = z;
            if (z > zMax) zMax = z;
        };

        for (let i = 0; i < st.frontier.length; i++) {
            const idx = st.frontier[i];
            const ox = st.originX[i];
            const oy = st.originY[i];
            const oz = st.originZ[i];
            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            const curHU = bg[idx];
            if (x > 0) tryAdvance(idx - 1, curHU, ox, oy, oz);
            if (x < nx - 1) tryAdvance(idx + 1, curHU, ox, oy, oz);
            if (y > 0) tryAdvance(idx - nx, curHU, ox, oy, oz);
            if (y < ny - 1) tryAdvance(idx + nx, curHU, ox, oy, oz);
            if (z > 0) tryAdvance(idx - nxny, curHU, ox, oy, oz);
            if (z < nz - 1) tryAdvance(idx + nxny, curHU, ox, oy, oz);
        }

        st.frontier = nextFrontier;
        st.originX = nextOriginX;
        st.originY = nextOriginY;
        st.originZ = nextOriginZ;

        if (count > 0) {
            scalars.modified();
            model.labelMap.modified();
            publicAPI.modified();
        }
        return { count, zMin: zMax < 0 ? -1 : zMin, zMax };
    };

    publicAPI.clearDilate = () => {
        model._dilateState = null;
    };

    // --------------------------------------------------------------------------

    // EDGE-INWARD EROSION (erase outside-in). The mirror of beginDilate/dilateStep: instead
    // of adding voxels beyond the mask boundary, it PEELS boundary voxels off, layer by
    // layer, toward the interior. beginErode finds the connected edit-label blob under the
    // click and seeds a frontier from its BOUNDARY; each erodeStep sets the in-band frontier
    // voxels to the empty label and the freshly-exposed edit-label neighbors become the next
    // (deeper) layer. State persists on model._erodeState across ticks. The caller owns the
    // stroke, so the whole hold is one undo. Returns false if the click isn't on an edit-label
    // voxel (caller falls back to the point-seeded erase).
    publicAPI.beginErode = (worldPoint, tolerance, options = {}) => {
        const editLabel = options.editLabel ?? model.paintLabel;
        // Empty value written where a voxel is peeled (passed in; axial = -1024).
        const emptyLabel = options.emptyLabel ?? 0;
        const driftMult = options.driftMult ?? 4;
        const maxRadius = options.maxRadiusVoxels ?? 60;
        const maxRadiusSq = maxRadius * maxRadius;
        if (!model.labelMap || !model.maskWorldToIndex || !model.backgroundImage) {
            return false;
        }
        const data = model.labelMap.getPointData().getScalars().getData();
        const bg = model.backgroundImage.getPointData().getScalars().getData();
        const [nx, ny, nz] = model.labelMap.getDimensions();
        if (nx === 0 || ny === 0 || nz === 0) return false;
        const nxny = nx * ny;

        const seedIndexPt = [0, 0, 0];
        vec3.transformMat4(seedIndexPt, worldPoint, model.maskWorldToIndex);
        const sx = Math.min(Math.max(Math.round(seedIndexPt[0]), 0), nx - 1);
        const sy = Math.min(Math.max(Math.round(seedIndexPt[1]), 0), ny - 1);
        const sz = Math.min(Math.max(Math.round(seedIndexPt[2]), 0), nz - 1);
        const seedIdx = sx + sy * nx + sz * nxny;

        // Erosion only applies when clicking ON an existing selection.
        if (data[seedIdx] !== editLabel) return false;

        const seedHU = bg[seedIdx];
        const stepTolerance = tolerance;
        const seedLo = seedHU - tolerance * driftMult;
        const seedHi = seedHU + tolerance * driftMult;

        // BFS the connected edit-label blob (marks membership; nothing is removed yet).
        const inBlob = new Uint8Array(nx * ny * nz);
        const blob = [seedIdx];
        inBlob[seedIdx] = 1;
        for (let i = 0; i < blob.length; i++) {
            const idx = blob[i];
            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            const pushIfBlob = (nIdx) => {
                if (!inBlob[nIdx] && data[nIdx] === editLabel) {
                    inBlob[nIdx] = 1;
                    blob.push(nIdx);
                }
            };
            if (x > 0) pushIfBlob(idx - 1);
            if (x < nx - 1) pushIfBlob(idx + 1);
            if (y > 0) pushIfBlob(idx - nx);
            if (y < ny - 1) pushIfBlob(idx + nx);
            if (z > 0) pushIfBlob(idx - nxny);
            if (z < nz - 1) pushIfBlob(idx + nxny);
        }

        // Frontier = blob voxels on the boundary (≥1 non-edit neighbor). Parallel arrays:
        // frontier idx + origin (the boundary voxel this branch peeled from, for the round
        // radius cap) + prevHU (the HU carried inward, for the re-anchored local step test).
        // queued[] guards against enqueuing a voxel twice as the frontier moves inward.
        const queued = new Uint8Array(nx * ny * nz);
        const frontier = [];
        const originX = [];
        const originY = [];
        const originZ = [];
        const prevHU = [];
        for (let i = 0; i < blob.length; i++) {
            const idx = blob[i];
            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            const onBoundary =
                (x > 0 && data[idx - 1] !== editLabel) ||
                (x < nx - 1 && data[idx + 1] !== editLabel) ||
                (y > 0 && data[idx - nx] !== editLabel) ||
                (y < ny - 1 && data[idx + nx] !== editLabel) ||
                (z > 0 && data[idx - nxny] !== editLabel) ||
                (z < nz - 1 && data[idx + nxny] !== editLabel);
            if (onBoundary) {
                frontier.push(idx);
                originX.push(x);
                originY.push(y);
                originZ.push(z);
                prevHU.push(seedHU);
                queued[idx] = 1;
            }
        }

        model._erodeState = {
            inBlob,
            queued,
            frontier,
            originX,
            originY,
            originZ,
            prevHU,
            maxRadiusSq,
            seedHU,
            stepTolerance,
            driftMult,
            seedLo,
            seedHi,
            editLabel,
            emptyLabel,
            nx,
            ny,
            nz,
            nxny,
        };
        return true;
    };

    // Peel the erosion frontier by ONE layer inward. Returns { count, zMin, zMax } for the
    // voxels REMOVED this step (count 0 means erosion can't proceed / is done). Pass a
    // tolerance to re-derive the band and maxRadiusVoxels to widen the round cap (grow-while-held).
    publicAPI.erodeStep = (tolerance, maxRadiusVoxels) => {
        const st = model._erodeState;
        if (!st) return { count: 0, zMin: -1, zMax: -1 };
        if (typeof tolerance === "number") {
            st.stepTolerance = tolerance;
            st.seedLo = st.seedHU - tolerance * st.driftMult;
            st.seedHi = st.seedHU + tolerance * st.driftMult;
        }
        if (typeof maxRadiusVoxels === "number") {
            st.maxRadiusSq = maxRadiusVoxels * maxRadiusVoxels;
        }
        const {
            inBlob,
            queued,
            editLabel,
            emptyLabel,
            stepTolerance,
            seedLo,
            seedHi,
            maxRadiusSq,
            nx,
            ny,
            nz,
            nxny,
        } = st;
        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        const bg = model.backgroundImage.getPointData().getScalars().getData();

        model.label = emptyLabel;

        const nextFrontier = [];
        const nextOriginX = [];
        const nextOriginY = [];
        const nextOriginZ = [];
        const nextPrevHU = [];
        let count = 0;
        let zMin = nz;
        let zMax = -1;

        for (let i = 0; i < st.frontier.length; i++) {
            const idx = st.frontier[i];
            // Skip if already removed (a voxel can be reached from two frontier voxels).
            if (data[idx] !== editLabel) continue;
            const ox = st.originX[i];
            const oy = st.originY[i];
            const oz = st.originZ[i];
            const ph = st.prevHU[i];
            const hu = bg[idx];
            // Band tests (same shape as dilation): local step vs the HU carried inward,
            // plus the wide clamp around the original seed. Out-of-band edge voxels are
            // LEFT in place (not peeled, not advanced past).
            if (Math.abs(hu - ph) > stepTolerance) continue;
            if (hu < seedLo || hu > seedHi) continue;
            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            // Round cap: don't peel deeper than maxRadius (Euclidean) from the origin edge.
            const dx = x - ox;
            const dy = y - oy;
            const dz = z - oz;
            if (dx * dx + dy * dy + dz * dz > maxRadiusSq) continue;

            // Peel: set to empty, record undo.
            strokeIndices.push(idx);
            strokeValues.push(data[idx]);
            data[idx] = emptyLabel;
            count++;
            if (z < zMin) zMin = z;
            if (z > zMax) zMax = z;

            // The freshly-exposed edit-label neighbors become the next (deeper) layer.
            const enqueue = (nIdx) => {
                if (!queued[nIdx] && inBlob[nIdx] && data[nIdx] === editLabel) {
                    queued[nIdx] = 1;
                    nextFrontier.push(nIdx);
                    nextOriginX.push(ox);
                    nextOriginY.push(oy);
                    nextOriginZ.push(oz);
                    nextPrevHU.push(hu);
                }
            };
            if (x > 0) enqueue(idx - 1);
            if (x < nx - 1) enqueue(idx + 1);
            if (y > 0) enqueue(idx - nx);
            if (y < ny - 1) enqueue(idx + nx);
            if (z > 0) enqueue(idx - nxny);
            if (z < nz - 1) enqueue(idx + nxny);
        }

        st.frontier = nextFrontier;
        st.originX = nextOriginX;
        st.originY = nextOriginY;
        st.originZ = nextOriginZ;
        st.prevHU = nextPrevHU;

        if (count > 0) {
            scalars.modified();
            model.labelMap.modified();
            publicAPI.modified();
        }
        return { count, zMin: zMax < 0 ? -1 : zMin, zMax };
    };

    publicAPI.clearErode = () => {
        model._erodeState = null;
    };

    // --------------------------------------------------------------------------

    publicAPI.startStroke = () => {
        strokeIndices = [];
        strokeValues = [];
    };

    // --------------------------------------------------------------------------

    // Mid-stroke rollback support (adaptive hold-grow): getStrokeMark() captures how many
    // voxels the open stroke has written so far; rollbackStrokeTo(mark) restores every
    // voxel written after that mark to its prior value and truncates the stroke arrays —
    // reverting ONE step without ending the stroke. Used to undo a runaway grow tick.
    publicAPI.getStrokeMark = () => strokeIndices.length;

    publicAPI.rollbackStrokeTo = (mark) => {
        if (!model.labelMap || mark >= strokeIndices.length) return;
        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        for (let k = strokeIndices.length - 1; k >= mark; k--) {
            data[strokeIndices[k]] = strokeValues[k];
        }
        strokeIndices.length = mark;
        strokeValues.length = mark;
        scalars.modified();
        model.labelMap.modified();
        publicAPI.modified();
    };

    // --------------------------------------------------------------------------

    // De-speckle the current (still-open) stroke: remove thin threads and tips left by the
    // HU grow. A voxel survives only if at least `minNeighbors` of its 6 face-neighbors are
    // also the edit label; thread voxels (1-2 neighbors) and dots are culled. Single pass:
    // all removals are decided against the PRE-cull state (order-independent), then applied.
    // Removals restore each voxel's ORIGINAL pre-stroke value and are recorded in the SAME
    // open stroke, so one undo reverts the whole hold (grow + cull). Call before endStroke().
    // Returns { removed, zMin, zMax } over the removed voxels.
    publicAPI.cullThinVoxels = (minNeighbors = 3) => {
        if (!model.labelMap) return { removed: 0, zMin: -1, zMax: -1 };
        const editLabel = model.paintLabel;
        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        const [nx, ny, nz] = model.labelMap.getDimensions();
        const nxny = nx * ny;

        // Earliest recorded pre-stroke value per index = its true original (an index can be
        // pushed more than once across ticks; the FIRST push holds the genuine prior value).
        const original = new Map();
        // Unique voxels currently at editLabel that the stroke touched (cull candidates).
        const candidates = [];
        const seen = new Set();
        for (let k = 0; k < strokeIndices.length; k++) {
            const idx = strokeIndices[k];
            if (!original.has(idx)) original.set(idx, strokeValues[k]);
            if (!seen.has(idx) && data[idx] === editLabel) {
                seen.add(idx);
                candidates.push(idx);
            }
        }

        // Decide removals against the current state (before applying any), so a voxel's fate
        // doesn't depend on whether a neighbor was culled earlier in the same pass.
        const toRemove = [];
        for (let i = 0; i < candidates.length; i++) {
            const idx = candidates[i];
            const z = (idx / nxny) | 0;
            const rem = idx - z * nxny;
            const y = (rem / nx) | 0;
            const x = rem - y * nx;
            let n = 0;
            if (x > 0 && data[idx - 1] === editLabel) n++;
            if (x < nx - 1 && data[idx + 1] === editLabel) n++;
            if (y > 0 && data[idx - nx] === editLabel) n++;
            if (y < ny - 1 && data[idx + nx] === editLabel) n++;
            if (z > 0 && data[idx - nxny] === editLabel) n++;
            if (z < nz - 1 && data[idx + nxny] === editLabel) n++;
            if (n < minNeighbors) toRemove.push(idx);
        }

        let zMin = nz;
        let zMax = -1;
        for (let i = 0; i < toRemove.length; i++) {
            const idx = toRemove[i];
            const prior = original.has(idx) ? original.get(idx) : data[idx];
            // Record the removal in the open stroke so undo reverts grow + cull together.
            strokeIndices.push(idx);
            strokeValues.push(data[idx]);
            data[idx] = prior;
            const z = (idx / nxny) | 0;
            if (z < zMin) zMin = z;
            if (z > zMax) zMax = z;
        }

        if (toRemove.length > 0) {
            scalars.modified();
            model.labelMap.modified();
            publicAPI.modified();
        }
        return {
            removed: toRemove.length,
            zMin: zMax < 0 ? -1 : zMin,
            zMax,
        };
    };

    // --------------------------------------------------------------------------

    publicAPI.endStroke = () => {
        const snapshot = new Array(strokeIndices.length);
        for (let k = 0; k < strokeIndices.length; k++) {
            snapshot[k] = [strokeIndices[k], strokeValues[k]];
        }
        pushToHistory(snapshot, model.label);
        strokeIndices = [];
        strokeValues = [];
        return Promise.resolve();
    };

    // --------------------------------------------------------------------------

    // Re-applies the in-progress stroke voxels if external state rewrites the labelMap mid-stroke.
    publicAPI.reapplyCurrentStroke = () => {
        if (!model.labelMap || strokeIndices.length === 0) return;
        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        for (let k = 0; k < strokeIndices.length; k++) {
            data[strokeIndices[k]] = model.label;
        }
        scalars.modified();
        model.labelMap.modified();
        publicAPI.modified();
    };

    // --------------------------------------------------------------------------

    // Paints one brush stamp by writing model.label to every voxel inside a 3D
    // ellipsoid centered at worldPoint. The ellipsoid (rather than a sphere)
    // accounts for per-axis voxel spacing so the brush stays circular in world
    // units. The three nested loops walk only voxels actually inside the
    // ellipsoid (shrinking each inner range) rather than the bounding cube.
    publicAPI.addPoint = (worldPoint) => {
        if (!model.labelMap || !model.maskWorldToIndex) return;

        const scalars = model.labelMap.getPointData().getScalars();
        const data = scalars.getData();
        const [nx, ny, nz] = model.labelMap.getDimensions();
        const spacing = model.labelMap.getSpacing();

        // Brush center in voxel-index space (rounded to nearest voxel).
        const centerIndex = [0, 0, 0];
        vec3.transformMat4(centerIndex, worldPoint, model.maskWorldToIndex);
        const cx = Math.round(centerIndex[0]);
        const cy = Math.round(centerIndex[1]);
        const cz = Math.round(centerIndex[2]);

        // Brush radii in voxel units along each axis (world radius / per-axis spacing).
        const radiusX = model.radius / spacing[0];
        const radiusY = model.radius / spacing[1];
        const radiusZ = model.radius / spacing[2];
        const label = model.label;

        // Lock the displayed slicing axis so the brush paints a single slice (2D disk)
        // instead of a 3D ellipsoid. Falls through to 3D when slicingMode is null/NONE.
        const lockedAxis =
            (model.slicingMode != null && model.slicingMode >= 0)
                ? model.slicingMode % 3
                : -1;

        // Iterate every voxel inside the ellipsoid
        //     (cx - x)² / rx² + (cy - y)² / ry² + (cz - z)² / rz² <= 1
        // by shrinking the inner x/y ranges using the ellipsoid equation.
        const zMin = lockedAxis === 2 ? cz : Math.max(0, Math.ceil(cz - radiusZ));
        const zMax = lockedAxis === 2 ? cz : Math.min(nz - 1, Math.floor(cz + radiusZ));
        for (let z = zMin; z <= zMax; z++) {
            const normZ = radiusZ > 0 ? (cz - z) / radiusZ : 0;
            if (normZ * normZ > 1) continue;

            // Cross-section of the ellipsoid at this z is an ellipse in (x, y) with reduced y-extent.
            const yExtent = radiusY * Math.sqrt(1 - normZ * normZ);
            const yMin = lockedAxis === 1 ? cy : Math.max(0, Math.ceil(cy - yExtent));
            const yMax = lockedAxis === 1 ? cy : Math.min(ny - 1, Math.floor(cy + yExtent));
            for (let y = yMin; y <= yMax; y++) {
                const normY = radiusY > 0 ? (cy - y) / radiusY : 0;
                if (normY * normY + normZ * normZ > 1) continue;

                // Line through the ellipsoid at this (y, z) has reduced x-extent.
                const xExtent = radiusX * Math.sqrt(1 - normY * normY - normZ * normZ);
                const xMin = lockedAxis === 0 ? cx : Math.max(0, Math.ceil(cx - xExtent));
                const xMax = lockedAxis === 0 ? cx : Math.min(nx - 1, Math.floor(cx + xExtent));
                for (let x = xMin; x <= xMax; x++) {
                    const voxelIndex = x + y * nx + z * nx * ny;
                    if (data[voxelIndex] === label) continue;

                    // Optional voxel filter: skip voxels that don't pass the user's predicate.
                    if (model.voxelFunc && model.backgroundImage) {
                        const bgScalars = model.backgroundImage.getPointData().getScalars();
                        const bgVoxel = [];
                        bgScalars.getTuple(voxelIndex, bgVoxel);
                        if (!model.voxelFunc(bgVoxel, voxelIndex, label)) continue;
                    }

                    // Record undo info, then write the new label.
                    strokeIndices.push(voxelIndex);
                    strokeValues.push(data[voxelIndex]);
                    data[voxelIndex] = label;
                }
            }
        }

        scalars.modified();
        model.labelMap.modified();
        publicAPI.modified();
    };

    // --------------------------------------------------------------------------

    publicAPI.canUndo = () => history.index > -1;

    // --------------------------------------------------------------------------

    publicAPI.applyLabelMap = (labelMap) => {
        const currentMapData = model.labelMap.getPointData().getScalars().getData();

        const newMapData = labelMap.getPointData().getScalars().getData();

        // Compute snapshot
        const snapshot = [];
        for (let i = 0; i < newMapData.length; ++i) {
            if (currentMapData[i] !== newMapData[i]) {
                snapshot.push([i, currentMapData[i]]);
            }
        }

        pushToHistory(snapshot, model.label);
        model.labelMap = labelMap;
        publicAPI.modified();
    };

    // --------------------------------------------------------------------------

    publicAPI.undo = () => {
        if (history.index > -1) {
            const scalars = model.labelMap.getPointData().getScalars();
            const data = scalars.getData();

            const snapshot = history.snapshots[history.index];
            for (let i = 0; i < snapshot.length; i++) {
                if (!snapshot[i]) {
                    break;
                }

                const [index, oldLabel] = snapshot[i];
                data[index] = oldLabel;
            }

            history.index--;

            scalars.setData(data);
            scalars.modified();
            model.labelMap.modified();
            publicAPI.modified();
        }
    };

    // --------------------------------------------------------------------------

    publicAPI.canRedo = () => history.index < history.labels.length - 1;

    // --------------------------------------------------------------------------

    publicAPI.redo = () => {
        if (history.index < history.labels.length - 1) {
            const scalars = model.labelMap.getPointData().getScalars();
            const data = scalars.getData();

            const redoLabel = history.labels[history.index + 1];
            const snapshot = history.snapshots[history.index + 1];

            for (let i = 0; i < snapshot.length; i++) {
                if (!snapshot[i]) {
                    break;
                }

                const [index] = snapshot[i];
                data[index] = redoLabel;
            }

            history.index++;

            scalars.setData(data);
            scalars.modified();
            model.labelMap.modified();
            publicAPI.modified();
        }
    };

    // --------------------------------------------------------------------------

    const superSetLabelMap = publicAPI.setLabelMap;
    publicAPI.setLabelMap = (lm) => {
        if (superSetLabelMap(lm)) {
            model.maskWorldToIndex = model.labelMap.getWorldToIndex();
            resetHistory();
            return true;
        }
        return false;
    };

    // --------------------------------------------------------------------------

    publicAPI.requestData = (_, outData) => {
        if (!model.backgroundImage) {
            debug(() => vtkErrorMacro('No background image found!'));
            return;
        }

        if (!model.backgroundImage.getPointData().getScalars()) {
            debug(() => vtkErrorMacro('Background image has no scalars'));
            return;
        }

        if (!model.labelMap) {
            // clone background image properties
            const labelMap = vtkImageData.newInstance(
                model.backgroundImage.get('spacing', 'origin', 'direction')
            );
            labelMap.setDimensions(model.backgroundImage.getDimensions());
            labelMap.computeTransforms();

            // right now only support 256 labels
            const values = new Uint8Array(model.backgroundImage.getNumberOfPoints());
            const dataArray = vtkDataArray.newInstance({
                numberOfComponents: 1, // labelmap with single component
                values,
            });
            labelMap.getPointData().setScalars(dataArray);

            publicAPI.setLabelMap(labelMap);
        }

        if (!model.maskWorldToIndex) {
            model.maskWorldToIndex = model.labelMap.getWorldToIndex();
        }

        const scalars = model.labelMap.getPointData().getScalars();

        if (!scalars) {
            debug(() => vtkErrorMacro('Mask image has no scalars'));
            return;
        }

        outData[0] = model.labelMap;
    };

    // --------------------------------------------------------------------------
    // Initialization
    // --------------------------------------------------------------------------

    resetHistory();
}

// ----------------------------------------------------------------------------
// Object factory
// ----------------------------------------------------------------------------

const DEFAULT_VALUES = {
    backgroundImage: null,
    labelMap: null,
    maskWorldToIndex: null,
    voxelFunc: null,
    radius: 1,
    label: 0,
    paintLabel: 1,
    slicingMode: null,
    paintingMode: SegmentationPaintingMode.BUMP
};

// ----------------------------------------------------------------------------

export function extend(publicAPI, model, initialValues = {}) {
    Object.assign(model, DEFAULT_VALUES, initialValues);

    // Make this a VTK object
    macro.obj(publicAPI, model);

    // Also make it an algorithm with no input and one output
    macro.algo(publicAPI, model, 0, 1);

    macro.setGet(publicAPI, model, [
        'backgroundImage',
        'labelMap',
        'maskWorldToIndex',
        'voxelFunc',
        'label',
        'paintLabel',
        'radius',
        'slicingMode',
        'paintingMode'
    ]);

    // Object specific methods
    vtkPaintFilter(publicAPI, model);
}

// ----------------------------------------------------------------------------

export const newInstance = macro.newInstance(extend, 'vtkPaintFilter');

// ----------------------------------------------------------------------------

export default { newInstance, extend };
